// src/features/statistics/entity-breakdowns.helpers.ts
//
// Pure helpers for the Analytics tab's Entity Distribution row, extracted so
// the population rules are unit-testable without mounting recharts.

import { formatCount } from "@/lib/format-count";
import type { StatisticsSnapshot } from "@/lib/types";

type Breakdowns = StatisticsSnapshot["entity_breakdowns"];

/**
 * IR-1414 r2: the "X of Y entities have location data" line under By Country.
 *
 * IR-1046 ⑤ had deliberately made Y the ALL-STATUS total (the sum of by_status),
 * so that this line and the By Status donut beside it would agree. That made
 * both of them disagree with everything else on the page: Tomer reopened
 * IR-1414 pointing at "1.3M" here and in the By Status centre while the Total
 * Entities tile and the By Platform donut both read 1.1M.
 *
 * The page now has one rule — every number counts live entities, except the By
 * Status chart, which exists precisely to show the deleted split and says so on
 * its face. So Y is the live total, and the backend's `entities_with_location`
 * is now a live-only count too (prod 2026-08-20: 546,262 live vs 5,113 deleted
 * entities carry a resolved country).
 *
 * `totalEntities` is the snapshot's own `kpis.total_entities`; the all-status
 * sum stays as a fallback so a pre-recompute snapshot still renders a line
 * rather than nothing.
 */
export function locationCoverageSubtitle(
  breakdowns: Breakdowns,
  totalEntities: number | undefined,
): string {
  const withLocation =
    breakdowns.entities_with_location ??
    breakdowns.by_country.reduce((s, r) => s + (Number(r.count) || 0), 0);

  const allStatusTotal = breakdowns.by_status.reduce((s, r) => s + (Number(r.count) || 0), 0);
  const denom = totalEntities ?? (allStatusTotal > 0 ? allStatusTotal : undefined);

  if (denom == null) return "";
  return `${formatCount(withLocation)} of ${formatCount(denom)} entities have location data`;
}

/**
 * IR-1414 r2: the By Status card is the ONE place on this page that counts
 * deleted entities, so it has to say so in both places a reader looks — the
 * label above the donut's centre total, and the card's own subtitle.
 *
 * The centre total is the honest sum of the donut's own slices; nothing about
 * the arithmetic was wrong. What was wrong is that it printed the word
 * "Entities" over 1.3M directly beneath a "Total Entities 1.1M" tile, which
 * reads as a contradiction rather than as a different population.
 */
export const BY_STATUS_CENTER_LABEL = "All statuses";

export function byStatusSubtitle(breakdowns: Breakdowns): string {
  const rows = breakdowns.by_status ?? [];
  const deleted = rows.find((r) => r.status === "deleted");
  if (!deleted?.count) return "";
  // Deliberately one short line: the subtitle sits above a fixed-height chart
  // body in a three-column grid, so a wrapped sentence here makes this card
  // taller than the two beside it. The full explanation lives in BY_STATUS_INFO,
  // rendered as the card's hover tooltip.
  return `Includes ${formatCount(deleted.count)} deleted`;
}

/**
 * Hover text for the By Status card, answering the question its bigger number
 * provokes: why does this card disagree with the tiles above it?
 */
export const BY_STATUS_INFO =
  "The only chart here that counts deleted entities. An entity that is removed " +
  "is kept in the database and marked deleted, so it can still be seen and " +
  "restored. Every other number on this page — the tiles, By Platform, " +
  "By Country — counts live entities only.";
